import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PersistConfig,
  PersistedState,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { applicationSlice } from 'store/application';

import { googleSlice, IGoogleState } from './google';
import { IUserSessionState, userSessionSlice } from './session';
import { IUserState, userConfigurationSlice } from './userConfiguration';

const USER_CFG_PERSIST_VERSION = 2;
const userConfigurationConfig: PersistConfig<IUserState> = {
  blacklist: ['transactionReportingPeriod', 'isLeaderTab'],
  key: '@userConfiguration',
  migrate: async (state) => {
    if (state && state._persist && state._persist.version !== USER_CFG_PERSIST_VERSION) {
      return {
        ...state,
        [userConfigurationSlice.name]: userConfigurationSlice.getInitialState(),
      } as PersistedState;
    }
    return state;
  },
  storage,
  version: USER_CFG_PERSIST_VERSION,
};

const USER_SESSION_PERSIST_VERSION = 1;
const userSessionConfigurationConfig: PersistConfig<IUserSessionState> = {
  key: '@userSession',
  migrate: async (state) => {
    if (
      state &&
      state._persist &&
      state._persist.version !== USER_SESSION_PERSIST_VERSION
    ) {
      return {
        ...state,
        [userConfigurationSlice.name]: userSessionSlice.getInitialState(),
      } as PersistedState;
    }
    return state;
  },
  storage,
  version: USER_SESSION_PERSIST_VERSION,
};

const GOOGLE_PERSIST_VERSION = 1;
const googleConfigurationConfig: PersistConfig<IGoogleState> = {
  key: '@google',
  migrate: async (state) => {
    if (state && state._persist && state._persist.version !== GOOGLE_PERSIST_VERSION) {
      return {
        ...state,
        [googleSlice.name]: googleSlice.getInitialState(),
      } as PersistedState;
    }
    return state;
  },
  storage,
  version: GOOGLE_PERSIST_VERSION,
};

export const rootReducer = combineReducers({
  [applicationSlice.name]: applicationSlice.reducer,
  [googleSlice.name]: persistReducer(googleConfigurationConfig, googleSlice.reducer),
  [userConfigurationSlice.name]: persistReducer(
    userConfigurationConfig,
    userConfigurationSlice.reducer,
  ),
  [userSessionSlice.name]: persistReducer(
    userSessionConfigurationConfig,
    userSessionSlice.reducer,
  ),
});

export const createStore = () => {
  const store = configureStore({
    devTools: process.env.NODE_ENV !== 'production',
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
    reducer: rootReducer,
  });

  return store;
};

const store = createStore();

export const persistor = persistStore(store);

export type TAppDispatch = typeof store.dispatch;
export type TRootState = ReturnType<typeof store.getState>;

export default store;
